
import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  EnhancedAlertDialogTitle, 
  EnhancedAlertDialogDescription,
  EnhancedAlertDialogAction,
  EnhancedAlertDialogCancel,
  EnhancedAlertDialogContent
} from '@/lib/shadcn-wrappers';
import { useTranslation } from 'react-i18next';

interface ConfirmationDialogProps {
  // Support both naming conventions
  open?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  title: string;
  description: string;
  cancelText?: string;
  confirmText?: string;
  onConfirm: () => void;
  isDanger?: boolean;
  isLoading?: boolean;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  isOpen,
  onOpenChange,
  onClose,
  title,
  description,
  cancelText,
  confirmText,
  onConfirm,
  isDanger = false,
  isLoading = false
}) => {
  const { t } = useTranslation();
  
  // Handle both naming conventions
  const isDialogOpen = open !== undefined ? open : (isOpen !== undefined ? isOpen : false);
  
  // Use fallback values from translations if not provided
  const finalCancelText = cancelText || t('interviewManagement.confirmations.cancel');
  const finalConfirmText = confirmText || t('interviewManagement.confirmations.confirm');
  
  // Create a handler that works with both patterns
  const handleOpenChange = (newOpenState: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpenState);
    } else if (onClose && !newOpenState) {
      onClose();
    }
  };

  const handleConfirmClick = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  return (
    <AlertDialog open={isDialogOpen} onOpenChange={handleOpenChange}>
      <EnhancedAlertDialogContent>
        <AlertDialogHeader>
          <EnhancedAlertDialogTitle>{title}</EnhancedAlertDialogTitle>
          <EnhancedAlertDialogDescription>{description}</EnhancedAlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <EnhancedAlertDialogCancel 
            disabled={isLoading}
            className="bg-gray-100 hover:bg-gray-200 text-gray-900 border border-gray-300"
          >
            {finalCancelText}
          </EnhancedAlertDialogCancel>
          <EnhancedAlertDialogAction 
            onClick={handleConfirmClick}
            disabled={isLoading}
            className={isDanger ? "bg-red-600 hover:bg-red-700 text-white" : "bg-green-600 hover:bg-green-700 text-white"}
          >
            {isLoading ? t('interviewManagement.confirmations.working') : finalConfirmText}
          </EnhancedAlertDialogAction>
        </AlertDialogFooter>
      </EnhancedAlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmationDialog;
